import type { InputBaseComponentProps } from "@mui/material/InputBase";
import React from "react";

type Props = InputBaseComponentProps & {
	component: React.FunctionComponent<InputBaseComponentProps>;
	options: Record<string, unknown>;
};

/**
 * Stripeの提供している CardNumberElement などのコンポーネントに対し、MaterialUiのスタイルを当てるために使うコンポーネント。
 *
 * ## 利用方法
 *
 * ```
 * <TextField
 *   variant="standard"
 *   InputProps={{
 *     inputComponent: StripeInputComponent,
 *     inputProps: {
 *       component: CardExpiryElement,
 *       options: { placeholder: '0000 0000 0000 0000' }
 *     }
 *   }}
 * />
 * ```
 *
 * ## 参考記事
 *
 * - Integration with 3rd party input libraries - mui.com
 *   - https://mui.com/material-ui/react-text-field/#integration-with-3rd-party-input-libraries
 * - How to use react-stripe-js? - mui/material-ui
 *   - https://github.com/mui/material-ui/issues/16037#issuecomment-1409431210
 */
export const StripeInputComponent = React.forwardRef(
	(props: Props, ref): JSX.Element => {
		const { component: Component, options, ...other } = props;
		const [mountNode, setMountNode] = React.useState<HTMLInputElement | null>(
			null,
		);

		React.useImperativeHandle(
			ref,
			() => ({
				focus: () => {
					if (mountNode === null) {
						return;
					}

					mountNode.focus();
				},
			}),
			[mountNode],
		);

		return (
			<Component
				onReady={setMountNode}
				options={{
					...options,
				}}
				{...other}
			/>
		);
	},
);

StripeInputComponent.displayName = "StripeInputComponent";
